import React, { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import {
  useTemplateSearch,
  type TemplateSearchResult,
  type TemplateSearchParams,
} from '@/hooks/schedule-change-requests/useScheduleChangeRequests';
import { useTemplateCalendarByRange } from '@/hooks/schedules/useTemplateCalendar';
import { type PreviewDay } from '@/utils/generateTemplatePreview';

// =====================================================
// Mini calendar preview
// =====================================================

const WEEKDAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const getDayColor = (day: PreviewDay): string => {
  if (!day.is_working_day) return 'bg-gray-200';
  if (day.schedule_type === 'night_shift') return 'bg-indigo-300';
  return 'bg-green-300';
};

const formatTime = (time: string | null | undefined): string => {
  if (!time) return '';
  const value = time.toString();
  const timePart = value.includes('T') ? value.split('T')[1] : value;
  return timePart.slice(0, 5);
};

const getDayLabel = (day: PreviewDay): string => {
  if (!day.is_working_day) return 'вых';
  const t = formatTime(day.shift_start);
  if (t) return t;
  return 'раб';
};

const isNightShift = (start: string | null | undefined): boolean => {
  const t = formatTime(start);
  if (!t) return false;
  const [h] = t.split(':').map(Number);
  return h >= 18 || h <= 6;
};

const MiniCalendarPreview: React.FC<{
  templateId: string;
}> = ({ templateId }) => {
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek) % 7 || 7;
    const start = new Date(now);
    start.setDate(now.getDate() + daysUntilMonday);
    const end = new Date(start);
    end.setDate(start.getDate() + 13);
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { startDate: fmt(start), endDate: fmt(end) };
  }, []);

  const { data, isLoading } = useTemplateCalendarByRange(
    templateId,
    startDate,
    endDate,
  );

  const preview: PreviewDay[] = useMemo(() => {
    if (!data?.calendar) return [];
    return [...data.calendar]
      .sort((a, b) => String(a.date).localeCompare(String(b.date)))
      .slice(0, 14)
      .map((c) => {
        const shiftStart = c.shift_start ? String(c.shift_start) : null;
        const shiftEnd = c.shift_end ? String(c.shift_end) : null;
        return {
          date: String(c.date).slice(0, 10),
          is_working_day: !!c.is_working_day,
          shift_start: shiftStart,
          shift_end: shiftEnd,
          schedule_type: !c.is_working_day
            ? 'day_off'
            : isNightShift(shiftStart)
              ? 'night_shift'
              : 'working',
        };
      });
  }, [data]);

  if (isLoading) {
    return (
      <div className="text-xs text-muted-foreground text-center py-4">
        Загрузка...
      </div>
    );
  }

  if (preview.length === 0) return null;

  const week1 = preview.slice(0, 7);
  const week2 = preview.slice(7, 14);

  return (
    <div className="space-y-1.5">
      <div className="text-[10px] text-muted-foreground font-medium">
        Паттерн на 2 недели:
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {WEEKDAY_SHORT.map((d) => (
          <div key={d} className="text-[9px] text-center text-muted-foreground font-medium">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {week1.map((day, i) => (
          <div
            key={i}
            className={cn(
              'rounded text-center py-0.5 text-[9px] font-medium',
              getDayColor(day),
              day.is_working_day ? 'text-gray-800' : 'text-gray-500',
            )}
          >
            {getDayLabel(day)}
          </div>
        ))}
      </div>
      {week2.length > 0 && (
        <div className="grid grid-cols-7 gap-0.5">
          {week2.map((day, i) => (
            <div
              key={i}
              className={cn(
                'rounded text-center py-0.5 text-[9px] font-medium',
                getDayColor(day),
                day.is_working_day ? 'text-gray-800' : 'text-gray-500',
              )}
            >
              {getDayLabel(day)}
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-3 pt-1">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded bg-green-300" />
          <span className="text-[9px] text-muted-foreground">День</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded bg-indigo-300" />
          <span className="text-[9px] text-muted-foreground">Ночь</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded bg-gray-200" />
          <span className="text-[9px] text-muted-foreground">Вых</span>
        </div>
      </div>
    </div>
  );
};

// =====================================================
// Main dialog
// =====================================================

interface TemplateSelectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeName: string;
  onSelect: (template: TemplateSearchResult) => void;
}

export const TemplateSelectDialog: React.FC<TemplateSelectDialogProps> = ({
  open,
  onOpenChange,
  employeeName,
  onSelect,
}) => {
  const [params, setParams] = useState<TemplateSearchParams>({});
  const [hoveredTemplate, setHoveredTemplate] = useState<TemplateSearchResult | null>(null);

  const hasFilters = !!(
    params.search ||
    params.workDays !== undefined ||
    params.offDays !== undefined ||
    params.shiftStart ||
    params.shiftEnd
  );

  const { data: templates, isLoading } = useTemplateSearch(params);

  const visibleTemplates = useMemo(() => {
    if (!hasFilters || !templates) return [];
    return templates;
  }, [hasFilters, templates]);

  const handleChange = (
    field: keyof TemplateSearchParams,
    value: string | number | undefined,
  ) => {
    setParams((prev) => ({
      ...prev,
      [field]: value === '' || value === undefined ? undefined : value,
    }));
  };

  const handleSelect = (template: TemplateSearchResult) => {
    onSelect(template);
    onOpenChange(false);
    setParams({});
    setHoveredTemplate(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Выбор графика для {employeeName}</DialogTitle>
          <DialogClose onClose={() => onOpenChange(false)} />
        </DialogHeader>

        <div className="flex gap-4">
          {/* Left: search + list */}
          <div className="flex-1 space-y-4 min-w-0">
            {/* Search inputs */}
            <div>
              <Label htmlFor="tsd-search" className="text-xs">
                Поиск по названию
              </Label>
              <Input
                id="tsd-search"
                placeholder="Например: 2/2 дневная..."
                value={params.search || ''}
                onChange={(e) => handleChange('search', e.target.value)}
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <div className="w-24">
                <Label htmlFor="tsd-work" className="text-xs">Рабочих</Label>
                <Input
                  id="tsd-work"
                  type="number"
                  min={1}
                  max={31}
                  placeholder="--"
                  value={params.workDays ?? ''}
                  onChange={(e) =>
                    handleChange('workDays', e.target.value ? Number(e.target.value) : undefined)
                  }
                />
              </div>
              <div className="w-24">
                <Label htmlFor="tsd-off" className="text-xs">Выходных</Label>
                <Input
                  id="tsd-off"
                  type="number"
                  min={0}
                  max={31}
                  placeholder="--"
                  value={params.offDays ?? ''}
                  onChange={(e) =>
                    handleChange('offDays', e.target.value ? Number(e.target.value) : undefined)
                  }
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="tsd-start" className="text-xs">Начало</Label>
                <Input
                  id="tsd-start"
                  type="time"
                  value={params.shiftStart || ''}
                  onChange={(e) => handleChange('shiftStart', e.target.value)}
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="tsd-end" className="text-xs">Конец</Label>
                <Input
                  id="tsd-end"
                  type="time"
                  value={params.shiftEnd || ''}
                  onChange={(e) => handleChange('shiftEnd', e.target.value)}
                />
              </div>
            </div>

            {/* Results list */}
            <div className="border rounded-md max-h-[300px] overflow-y-auto">
              {!hasFilters ? (
                <p className="p-4 text-sm text-muted-foreground text-center">
                  Введите параметры для поиска графика
                </p>
              ) : isLoading ? (
                <p className="p-4 text-sm text-muted-foreground text-center">
                  Загрузка...
                </p>
              ) : visibleTemplates.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground text-center">
                  Графики не найдены
                </p>
              ) : (
                <div className="divide-y">
                  {visibleTemplates.map((t) => {
                    const timeStr =
                      t.shift_start && t.shift_end
                        ? `${t.shift_start.slice(0, 5)}-${t.shift_end.slice(0, 5)}`
                        : '';
                    const isHovered = hoveredTemplate?.id === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => handleSelect(t)}
                        onMouseEnter={() => setHoveredTemplate(t)}
                        onMouseLeave={() => setHoveredTemplate(null)}
                        className={cn(
                          'w-full flex items-center justify-between px-4 py-3 text-left transition-colors',
                          isHovered ? 'bg-blue-50' : 'hover:bg-accent',
                        )}
                      >
                        <div>
                          <div className="font-medium text-sm">{t.name}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                              {t.work_days}/{t.off_days}
                            </Badge>
                            {timeStr && (
                              <span className="text-xs text-muted-foreground">{timeStr}</span>
                            )}
                            <span className="text-xs text-muted-foreground capitalize">
                              {t.recurrence_type}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs text-blue-600">Выбрать</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right: preview panel */}
          <div className="w-[240px] shrink-0">
            <div className="border rounded-lg p-3 bg-muted/30 sticky top-0">
              {hoveredTemplate ? (
                <>
                  <div className="font-medium text-sm mb-2 truncate">
                    {hoveredTemplate.name}
                  </div>
                  <MiniCalendarPreview templateId={hoveredTemplate.id} />
                </>
              ) : (
                <p className="text-xs text-muted-foreground text-center py-6">
                  Наведите на график чтобы увидеть паттерн
                </p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
